import { createFileRoute } from "@tanstack/react-router";
import { BackButton } from "@/components/site/BackButton";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — KAKAIYS" },
      {
        name: "description",
        content:
          "How KAKAIYS collects, uses and protects your personal information. Read our full privacy policy.",
      },
      { property: "og:title", content: "Privacy Policy — KAKAIYS" },
      { property: "og:description", content: "How KAKAIYS protects your personal information." },
      { property: "og:url", content: "https://kakaiys.com/privacy" },
    ],
      links: [{ rel: "canonical", href: "https://kakaiys.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <article className="pt-40 pb-24 container-luxe max-w-3xl">
      <BackButton fallback="/" className="mb-8" />
      <div className="eyebrow mb-5">Maison · Privacy</div>
      <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-4">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-12">Last updated: April 2026</p>

      <div className="space-y-10 text-foreground/85">
        <section>
          <p>
            KAKAIYS ("we", "us", "our") respects your privacy. This Privacy Policy explains how we collect,
            use, disclose and safeguard your information when you visit our website or make a purchase.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">1. Information we collect</h2>
          <p className="mb-2">We collect the following categories of information:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Account data:</strong> name, email, password (encrypted).</li>
            <li><strong>Order data:</strong> shipping address, billing address, phone number, items purchased.</li>
            <li><strong>Payment data:</strong> processed securely by our payment provider (2Checkout / Verifone). We never store full card numbers on our servers.</li>
            <li><strong>Technical data:</strong> IP address, browser type, device, pages viewed, referral source.</li>
            <li><strong>Marketing data:</strong> email preferences and subscription status.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">2. How we use your information</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>To process and fulfil your orders</li>
            <li>To communicate about your order status and shipping updates</li>
            <li>To respond to customer service inquiries</li>
            <li>To send marketing emails (only if you opt in — you may unsubscribe at any time)</li>
            <li>To improve our website, products and services</li>
            <li>To detect, prevent and address fraud or technical issues</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">3. Sharing your information</h2>
          <p>We share your information only with trusted third parties who help us operate our business:</p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong>Payment processors</strong> (2Checkout / Verifone) to process payments securely.</li>
            <li><strong>Shipping partners</strong> (DHL, FedEx, local couriers) to deliver your order.</li>
            <li><strong>Email service providers</strong> for transactional and marketing emails.</li>
            <li><strong>Analytics providers</strong> to understand site usage in aggregate.</li>
          </ul>
          <p className="mt-2">We never sell your personal information to third parties.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">4. Cookies</h2>
          <p>
            We use cookies and similar technologies to remember your preferences, keep you logged in, and
            understand how you use our site. You may disable cookies in your browser, but some site
            features may not work properly.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">5. Data retention</h2>
          <p>
            We retain your personal data for as long as your account is active, or as needed to provide
            services, comply with legal obligations, resolve disputes and enforce agreements.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">6. Legal basis for processing (GDPR)</h2>
          <p>If you are in the European Economic Area or the United Kingdom, we rely on the following legal bases under the GDPR:</p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong>Performance of a contract</strong> — to process and ship your order, handle returns and provide customer service.</li>
            <li><strong>Legitimate interests</strong> — to prevent fraud, secure our site, improve our products and services, and communicate with you about your account.</li>
            <li><strong>Consent</strong> — for marketing emails and non-essential cookies. You may withdraw consent at any time.</li>
            <li><strong>Legal obligation</strong> — to comply with tax, accounting, customs and consumer-protection laws.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">7. International transfers</h2>
          <p>
            Your data may be processed in Pakistan and in countries where our service providers operate
            (including the EU, UK and US). Where required, we use appropriate safeguards such as Standard
            Contractual Clauses to ensure your data receives an equivalent level of protection.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">8. Your rights</h2>
          <p>Depending on your jurisdiction (GDPR, CCPA, etc.) you may have the right to:</p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>Access the personal data we hold about you</li>
            <li>Request correction or deletion of your data</li>
            <li>Object to or restrict processing</li>
            <li>Request data portability</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p className="mt-2">
            To exercise any of these rights, contact us at <a className="text-gold underline-grow" href="mailto:privacy@kakaiys.com">privacy@kakaiys.com</a>.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">9. Security</h2>
          <p>
            We implement industry-standard security measures including encryption in transit (TLS) and at
            rest, secure authentication and access controls. However, no method of transmission over the
            internet is 100% secure.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">10. Children's privacy</h2>
          <p>
            Our services are not directed to children under 16. We do not knowingly collect personal
            information from minors.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">11. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. The "Last updated" date at the top reflects the
            most recent revision. Material changes will be notified via email or a site banner.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">12. Data Protection contact</h2>
          <p>
            For any privacy questions, data access requests, or to contact our Data Protection point of
            contact, email <a className="text-gold underline-grow" href="mailto:privacy@kakaiys.com">privacy@kakaiys.com</a>.
            EU/UK residents also have the right to lodge a complaint with their local data protection
            authority.
          </p>
        </section>
      </div>
    </article>
  );
}
